import React from 'react';
import {
  Box,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  FavoriteBorderOutlined as FavoriteBorderOutlinedIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import TimelineDaySnapshotCard from './TimelineDaySnapshotCard.jsx';
import UnifiedTimelineEntry from './UnifiedTimelineEntry';
import TimeGroupHeader from './TimeGroupHeader';
import SourceNoteTimelineGroup from './SourceNoteTimelineGroup';
import TimelineDisclosureToggle from './TimelineDisclosureToggle';
import StoryMomentParentInline from './StoryMomentParentInline';
import {
  getCaregiverLabel,
  getResolvedCaregiverRole,
} from './utils/timelineCaregiverLabels';

const TEXT_PRIMARY = '#1F2937';
const TEXT_SECONDARY = '#4B5563';

const isStoryMomentReferenceEntry = (entry = {}) => (
  entry.kind === 'story_moment_reference'
  || entry.pointerKind === 'story_moment_reference'
);

const isStoryMomentReferenceGroup = (categoryGroup = {}) => (
  Array.isArray(categoryGroup.entries)
  && categoryGroup.entries.length > 0
  && categoryGroup.entries.every((entry) => isStoryMomentReferenceEntry(entry))
);

export const getTimelineDayLabel = (selectedDate = null) => (
  selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
    ? selectedDate.toLocaleDateString('en-US', { weekday: 'long' })
    : 'this day'
);

export const getEmptyTimelineButtonLabel = (selectedDate = null) => (
  `+ Log something for ${getTimelineDayLabel(selectedDate)}`
);

const TimelineEmptyState = ({
  mobileTimelineMinHeight,
  onEmptyStateClick,
  selectedDate = null,
  maxWidth = 520,
  dense = false,
  accent = null,
}) => {
  const dayLabel = getTimelineDayLabel(selectedDate);
  const activeAccent = accent || { primary: colors.brand.ink, soft: colors.landing.sageLight };

  return (
    <Box
      onClick={onEmptyStateClick}
      onKeyDown={(event) => {
        if ((event.key === 'Enter' || event.key === ' ') && onEmptyStateClick) {
          event.preventDefault();
          onEmptyStateClick();
        }
      }}
      role={onEmptyStateClick ? 'button' : undefined}
      tabIndex={onEmptyStateClick ? 0 : undefined}
      sx={{
        minHeight: mobileTimelineMinHeight,
        textAlign: 'center',
        py: dense ? { xs: 1.35, md: 1.6 } : { xs: 4.25, md: 5 },
        px: dense ? { xs: 1.2, md: 1.5 } : { xs: 1.5, md: 2 },
        borderRadius: dense ? '12px' : '14px',
        cursor: onEmptyStateClick ? 'pointer' : 'default',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': onEmptyStateClick
          ? {
              backgroundColor: 'action.hover',
              boxShadow: `0 2px 8px ${colors.app.cards.shadowPanel}`,
            }
          : undefined,
        '&:focus-visible': onEmptyStateClick
          ? {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            }
          : undefined,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: dense ? 0.75 : 1.2,
        maxWidth,
        mx: 'auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: dense ? 40 : 54,
          height: dense ? 40 : 54,
          borderRadius: '18px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: activeAccent.soft,
          color: activeAccent.primary,
        }}
      >
        <FavoriteBorderOutlinedIcon sx={{ fontSize: dense ? 20 : 25 }} />
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 850, mb: 0.2, color: TEXT_PRIMARY, textAlign: 'center', fontSize: dense ? '0.95rem' : { xs: '1.35rem', sm: '1.5rem' }, lineHeight: 1.1 }}>
        Nothing logged on {dayLabel}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: dense ? '0.72rem' : { xs: '0.95rem', sm: '1rem' },
          lineHeight: dense ? 1.42 : 1.48,
          color: TEXT_SECONDARY,
          maxWidth: dense ? 230 : 310,
          mx: 'auto',
          textAlign: 'center',
        }}
      >
        Add the first update for this day.
      </Typography>
    </Box>
  );
};

const StandardCategoryGroup = ({
  actionMenuDisabled,
  categoryGroup,
  currentUserId,
  currentUserRole,
  getEntryPresentation,
  onDeleteEntry,
  onEditEntry,
  child = null,
  expandedStoryParentId = '',
  onToggleStoryParent = null,
}) => {
  const [expanded, setExpanded] = React.useState(true);
  const firstPresentation = getEntryPresentation(categoryGroup.entries[0]) || {};
  const categoryPalette = firstPresentation?.careLogMeta?.tone || firstPresentation?.categoryPalette || null;
  const CategoryIconComponent = firstPresentation?.careLogMeta?.iconComponent || null;
  const categoryIcon = firstPresentation?.careLogMeta?.icon || null;
  const entries = [...categoryGroup.entries];
  const featuredEntry = entries[0];
  const supportingEntries = entries.slice(1);

  if (isStoryMomentReferenceGroup(categoryGroup)) {
    return (
      <Stack spacing={0.72} sx={{ px: { xs: 1.1, md: 1.4 }, py: { xs: 1.0, md: 1.15 } }}>
        {entries.map((entry, index) => (
          <Box
            key={`${categoryGroup.key}-${entry.id}-${index}`}
            sx={{
              overflow: 'hidden',
              borderRadius: { xs: '16px', md: '18px' },
              border: `1px solid ${alpha(colors.semantic.line, 0.78)}`,
              bgcolor: '#FFFFFF',
              boxShadow: 'none',
              px: { xs: 1.1, md: 1.35 },
              py: { xs: 0.95, md: 1.0 },
            }}
          >
            <UnifiedTimelineEntry
              entry={entry}
              presentation={getEntryPresentation(entry)}
              timeString={entry.narrativeTimeLabel || null}
              isLast={index === entries.length - 1}
              showActions={false}
              onEditEntry={null}
              onDeleteEntry={null}
              actionMenuDisabled={Boolean(actionMenuDisabled)}
              isEditing={false}
              editText=""
              onEditTextChange={null}
              onSaveEdit={null}
              onCancelEdit={null}
              layoutVariant="feed"
              caregiverLabel={getCaregiverLabel(entry)}
              caregiverRole={getResolvedCaregiverRole(entry, currentUserRole, currentUserId)}
              currentUserRole={currentUserRole}
              child={child}
              showCaptureFirstOriginalNote={false}
              showCaptureFirstContextDetails={false}
              storyParentExpanded={expandedStoryParentId === entry.id}
              onStoryParentToggle={() => onToggleStoryParent?.(entry.id)}
              sourceDetailsNode={(
                <StoryMomentParentInline
                  entry={entry}
                  expanded={expandedStoryParentId === entry.id}
                  getEntryPresentation={getEntryPresentation}
                  onCollapse={() => onToggleStoryParent?.(entry.id)}
                />
              )}
            />
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderRadius: { xs: '18px', md: '20px' },
        borderColor: alpha(colors.semantic.line, 0.78),
        bgcolor: '#FFFFFF',
        boxShadow: 'none',
      }}
    >
      <TimelineDisclosureToggle
        expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
        label={categoryGroup.label}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          textAlign: 'left',
          px: { xs: 1.35, md: 1.55 },
          py: 0.72,
          display: 'flex',
          alignItems: 'center',
          gap: 1.05,
          borderBottom: expanded ? `1px solid ${alpha(colors.semantic.line, 0.85)}` : 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.05, minWidth: 0, flex: '1 1 auto' }}>
          <Box
            sx={{
              minWidth: 36,
              height: 36,
              px: 1,
              borderRadius: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: categoryPalette?.bg || alpha(colors.semantic.sageDeep, 0.12),
              color: categoryPalette?.text || colors.semantic.sageDeep,
              border: `1px solid ${alpha(categoryPalette?.border || colors.semantic.line, 0.8)}`,
              flexShrink: 0,
            }}
          >
            {CategoryIconComponent ? (
              <CategoryIconComponent sx={{ fontSize: 18, color: categoryPalette?.text || colors.semantic.sageDeep }} />
            ) : (
              <Typography sx={{ fontSize: '1rem', lineHeight: 1, fontWeight: 800, color: categoryPalette?.text || colors.semantic.sageDeep }}>
                {categoryIcon || '•'}
              </Typography>
            )}
          </Box>
        </Box>
      </TimelineDisclosureToggle>

      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Stack spacing={0.72} sx={{ px: { xs: 1.1, md: 1.4 }, py: { xs: 1.0, md: 1.15 } }}>
          {featuredEntry ? (
            <Box sx={{ pb: supportingEntries.length ? 0.15 : 0 }}>
              <UnifiedTimelineEntry
                entry={featuredEntry}
                presentation={getEntryPresentation(featuredEntry)}
                timeString={featuredEntry.narrativeTimeLabel || null}
                isLast={!supportingEntries.length}
                showActions
                onEditEntry={onEditEntry}
                onDeleteEntry={() => onDeleteEntry(featuredEntry)}
                actionMenuDisabled={Boolean(actionMenuDisabled)}
                isEditing={false}
                editText=""
                onEditTextChange={null}
                onSaveEdit={null}
                onCancelEdit={null}
                layoutVariant="story"
                caregiverLabel={getCaregiverLabel(featuredEntry)}
                caregiverRole={getResolvedCaregiverRole(featuredEntry, currentUserRole, currentUserId)}
                currentUserRole={currentUserRole}
                child={child}
              />
            </Box>
          ) : null}

          {supportingEntries.length ? (
            <Box
              sx={{
                position: 'relative',
                pl: { xs: 0, md: 0 },
                ml: { xs: 0, md: 0 },
                pt: 0.05,
              }}
            >
              <Stack spacing={0.72}>
                {supportingEntries.map((entry, index) => {
                  const presentation = getEntryPresentation(entry);
                  const caregiverLabel = getCaregiverLabel(entry);
                  const caregiverRole = getResolvedCaregiverRole(entry, currentUserRole, currentUserId);

                  return (
                    <UnifiedTimelineEntry
                      key={`${categoryGroup.key}-${entry.id}-${index + 1}`}
                      entry={entry}
                      presentation={presentation}
                      timeString={entry.narrativeTimeLabel || null}
                      isLast={index === supportingEntries.length - 1}
                      showActions
                      onEditEntry={onEditEntry}
                      onDeleteEntry={() => onDeleteEntry(entry)}
                      actionMenuDisabled={Boolean(actionMenuDisabled)}
                      isEditing={false}
                      editText=""
                      onEditTextChange={null}
                      onSaveEdit={null}
                      onCancelEdit={null}
                      layoutVariant="story"
                      caregiverLabel={caregiverLabel}
                      caregiverRole={caregiverRole}
                      currentUserRole={currentUserRole}
                      child={child}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Collapse>
    </Paper>
  );
};

const NarrativeCategoryGroup = (props) => (
  props.categoryGroup.sourceGroup ? (
    <SourceNoteTimelineGroup {...props} />
  ) : (
    <StandardCategoryGroup {...props} />
  )
);

const getSourceReviewGroupKey = (categoryGroup = {}) => (
  categoryGroup.sourceContext?.sourceNoteId
  || categoryGroup.sourceContext?.sourceNotePath
  || categoryGroup.entries?.[0]?.metadata?.sourceNoteId
  || categoryGroup.entries?.[0]?.sourceNoteId
  || ''
);

const TimelineNarrativeSections = ({
  actionMenuDisabled,
  beforeSections = null,
  afterSections = null,
  currentUserId = null,
  currentUserRole = null,
  child = null,
  getEntryPresentation = () => ({}),
  groupedNarrativeBlocks = [],
  hasSupplementalTimelineContent = false,
  profileReceiptSourceIds = [],
  mobileTimelineMinHeight,
  desktopFeed = false,
  daySnapshot = null,
  showDaySummary = true,
  onDeleteEntry,
  onEditEntry,
  onEmptyStateClick,
  selectedDate = null,
  accent = null,
}) => {
  const [expandedStoryParentId, setExpandedStoryParentId] = React.useState('');
  const handleToggleStoryParent = React.useCallback((entryId) => {
    setExpandedStoryParentId((current) => current === entryId ? '' : entryId);
  }, []);

  React.useEffect(() => {
    setExpandedStoryParentId('');
  }, [selectedDate]);

  if (!groupedNarrativeBlocks.length) {
    return (
      <Box
        sx={{
          mt: desktopFeed ? 1.25 : 1.1,
          minHeight: mobileTimelineMinHeight,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {beforeSections}
        {!hasSupplementalTimelineContent ? (
          <TimelineEmptyState
            mobileTimelineMinHeight={mobileTimelineMinHeight}
            onEmptyStateClick={onEmptyStateClick}
            selectedDate={selectedDate}
            dense={desktopFeed}
            accent={accent}
          />
        ) : null}
        {afterSections}
      </Box>
    );
  }

  let latestSourceGroupKey = '';
  groupedNarrativeBlocks.forEach((block) => {
    (block.categoryGroups || []).forEach((categoryGroup) => {
      if (categoryGroup?.sourceGroup) {
        latestSourceGroupKey = categoryGroup.key;
      }
    });
  });

  const lastSourceReviewGroupBySource = new Map();
  groupedNarrativeBlocks.forEach((block) => {
    (block.categoryGroups || []).forEach((categoryGroup) => {
      if (!categoryGroup?.sourceGroup) return;
      const sourceReviewKey = getSourceReviewGroupKey(categoryGroup);
      if (sourceReviewKey) {
        lastSourceReviewGroupBySource.set(sourceReviewKey, categoryGroup.key);
      }
    });
  });
  const lastSourceReviewGroupKeys = new Set(lastSourceReviewGroupBySource.values());
  const profileReceiptSourceIdSet = new Set(profileReceiptSourceIds);
  const content = (
    <Stack spacing={1.15}>
      {groupedNarrativeBlocks.map((block) => {
        return (
        <Box
          key={block.key}
        >
          <Box sx={{ px: { xs: 1.35, md: 1.65 }, pt: { xs: 1.1, md: 1.35 }, pb: 0.9 }}>
            <TimeGroupHeader
              label={block.label}
              period={block.period || block.timeBlock}
              hint={block.hint}
              entryCount={block.entries.length}
              />
            </Box>

          <Stack spacing={0.9} sx={{ px: { xs: 0.0, md: 0.0 }, pb: { xs: 1.1, md: 1.4 } }}>
            {block.categoryGroups.map((categoryGroup) => {
              const sourceReviewKey = getSourceReviewGroupKey(categoryGroup);
              const sourceHasProfileReceipt = sourceReviewKey
                && profileReceiptSourceIdSet.has(sourceReviewKey);

              return (
              <NarrativeCategoryGroup
                key={categoryGroup.key}
                actionMenuDisabled={actionMenuDisabled}
                categoryGroup={categoryGroup}
                currentUserId={currentUserId}
                currentUserRole={currentUserRole}
                child={child}
                getEntryPresentation={getEntryPresentation}
                showOriginalPanel={
                  !sourceHasProfileReceipt
                  && (!categoryGroup.sourceGroup || lastSourceReviewGroupKeys.has(categoryGroup.key))
                }
                showSourceReviewActions={
                  !categoryGroup.sourceGroup || lastSourceReviewGroupKeys.has(categoryGroup.key)
                }
                requireKnowledgeItems={
                  categoryGroup.sourceGroup && lastSourceReviewGroupKeys.has(categoryGroup.key)
                }
                defaultExpanded={categoryGroup.key === latestSourceGroupKey && !categoryGroup.storyItem}
                onDeleteEntry={onDeleteEntry}
                onEditEntry={onEditEntry}
                expandedStoryParentId={expandedStoryParentId}
                onToggleStoryParent={handleToggleStoryParent}
              />
              );
            })}
          </Stack>
        </Box>
        );
      })}
    </Stack>
  );

  if (desktopFeed) {
    return (
      <Box
        sx={{
          mt: 1.25,
          minHeight: mobileTimelineMinHeight,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {beforeSections}
        {showDaySummary ? <TimelineDaySnapshotCard embedded daySnapshot={daySnapshot} /> : null}
        {content}
        {afterSections}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 1.1,
        minHeight: mobileTimelineMinHeight,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {beforeSections}
      {showDaySummary ? <TimelineDaySnapshotCard daySnapshot={daySnapshot} /> : null}
      {content}
      {afterSections}
    </Box>
  );
};

export default TimelineNarrativeSections;
